import React, { useCallback, useEffect, useState } from 'react'
import { Alert } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { Link, Navigate } from 'react-router-dom'
import Loading from '../../components/loading'
import useApi from '../../hooks/useApi'
import {
  charityPublicProfilePath,
  userManagedCharityRegistrations,
} from '../../lib/charityProfileLink'

/**
 * Resolves "my charity profile" for the nav — same picker/redirect pattern as
 * /charity-account, but targets the public profile URL.
 */
export default function CharityProfileRedirect() {
  const { t } = useTranslation()
  const { getUserCharityRegistrations } = useApi()

  const [verifiedCharities, setVerifiedCharities] = useState([])
  const [loading, setLoading] = useState(true)

  const fetchRegistrations = useCallback(async () => {
    setLoading(true)
    const { data } = await getUserCharityRegistrations()
    setVerifiedCharities(userManagedCharityRegistrations(data))
    setLoading(false)
  }, [getUserCharityRegistrations])

  useEffect(() => {
    fetchRegistrations()
  }, [fetchRegistrations])

  if (loading) {
    return <Loading show />
  }

  if (verifiedCharities.length === 0) {
    return (
      <div className="container mt-4">
        <Alert variant="info">
          {t('charities.settings.no_verified_charities')}
        </Alert>
        <Link to="/charities">{t('charities.settings.back_to_charities')}</Link>
      </div>
    )
  }

  if (verifiedCharities.length === 1) {
    const path = charityPublicProfilePath(verifiedCharities[0].charity?.id)
    if (path) {
      return <Navigate to={path} replace />
    }
  }

  return (
    <div className="container mt-4 mb-5">
      <h1 className="fs-1">{t('charities.profile.select_charity_title')}</h1>
      <p className="text-muted">
        {t('charities.profile.select_charity_message')}
      </p>
      <ul className="list-group">
        {verifiedCharities.map(registration => {
          const charity = registration.charity
          const path = charityPublicProfilePath(charity?.id)
          if (!path) return null
          return (
            <li className="list-group-item" key={registration.id}>
              <Link to={path}>
                {charity?.organization_name}
                {charity?.ein ? ` (EIN ${charity.ein})` : ''}
              </Link>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
